import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Admin/Sidebar';
import useStudentsStore from '../../stores/studentsStore';
import useSubjectStore from '../../stores/subjectStore';
import useGroupsStore from '../../stores/groupsStore';
import PaymentCalendar from '../../components/Admin/User/PaymentCalendar';
import { getStudentPayments, updateStudentPayments } from '../../api';
import AdminBottomNavBar from '../../components/Admin/AdminBottomNavBar';
import { getStaticBaseUrl } from '../../stores/configStore';

function AdminUserFull() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { students, fetchStudents, updateStudent, deleteStudent, resetStudentPassword } = useStudentsStore();
  const { subjects, fetchSubjects } = useSubjectStore();
  const { groups, fetchGroups } = useGroupsStore();
  const [user, setUser] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [editName, setEditName] = useState('');
  const [editGroup, setEditGroup] = useState('');
  const [editGroups, setEditGroups] = useState([]);
  const [groupsDdOpen, setGroupsDdOpen] = useState(false);
  const [groupsSearch, setGroupsSearch] = useState('');
  const groupsDdRef = useRef(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    if (!students.length) fetchStudents();
    if (!subjects.length) fetchSubjects();
    if (!groups.length) fetchGroups();
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (groupsDdRef.current && !groupsDdRef.current.contains(e.target)) {
        setGroupsDdOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const found = students.find(s => String(s._id) === String(id));
    setUser(found || null);
    setEditName(found?.fullName || '');
    setEditGroup(found?.group?._id || '');
    setEditGroups(Array.isArray(found?.groups) ? found.groups.map(g => g._id || g) : []);
    setAdminNotes(found?.adminNotes || '');
  }, [students, id]);

  useEffect(() => {
    async function fetchPayments() {
      setLoadingPayments(true);
      try {
        const data = await getStudentPayments({ studentId: id });
        setPaymentHistory(data);
      } catch {
        setPaymentHistory([]);
      } finally {
        setLoadingPayments(false);
      }
    }
    fetchPayments();
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await updateStudentPayments({ studentId: id, paymentHistory });
      setSaveMsg('Сохранено!');
    } catch (e) {
      setSaveMsg('Ошибка сохранения: ' + e.message);
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(''), 2000);
    }
  };

  return (
    <div className='AdminUserFull'>
      <div className="AdminUserFull__container">
        <Sidebar className="admin-sidebar" />
        <div className="content_admin admin-user-full-wrapper">
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="admin-user-back-btn" onClick={() => navigate(-1)}>Назад</button>
            <button className="admin-user-back-btn" onClick={() => navigate(`/admin/user/${id}/attendance`)} style={{ background: '#6c3be4', color: '#fff' }}>Посещаемость ученика</button>
          </div>
          <h2 className="admin-user-title">Полная информация о пользователе</h2>
          <div className="admin-user-full-flex">
            {/* Левая колонка */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {user ? (
                <div className="admin-user-full-info" style={{marginBottom: 24}}>
                  <div style={{marginBottom: 18}}>
                    <label style={{fontWeight: 700, fontSize: 18, marginRight: 12, minWidth: 70, display: 'inline-block'}}>ФИО:</label>
                    <div className="admin-user-full-name-row">
                      <input
                        type="text"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        style={{padding: '8px 14px', borderRadius: 8, border: '1.5px solid #d1d1e0', fontSize: 17, flex: 1, maxWidth: 320, background: '#fafaff'}}
                      />
                      <button
                        style={{background: '#1a7f1a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 22px', fontWeight: 500, fontSize: 16, cursor: actionLoading || !editName.trim() || editName === user.fullName ? 'not-allowed' : 'pointer', transition: 'background 0.2s'}}
                        disabled={actionLoading || !editName.trim() || editName === user.fullName}
                        onClick={async () => {
                          setActionLoading(true);
                          try {
                            await updateStudent(user._id, { fullName: editName });
                          } finally {
                            setActionLoading(false);
                          }
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#188018'}
                        onMouseOut={e => e.currentTarget.style.background = '#1a7f1a'}
                      >Сохранить</button>
                    </div>
                  </div>
                  <div style={{marginBottom: 18, fontSize: 16}}><b>ID:</b> {user._id}</div>
                  <div style={{marginBottom: 18, fontSize: 16}}><b>Логин:</b> {user.login || <span style={{color:'#888'}}>нет данных</span>}</div>
                  <div style={{marginBottom: 18, fontSize: 16}}>
                    <b>Пароль:</b> {resetPassword
                      ? <span style={{color:'#1a7f1a', fontWeight:600}}>{resetPassword}</span>
                      : <span style={{color:'#888'}}>Пароль не хранится в открытом виде. Если утерян — сбросьте через кнопку ниже.</span>}
                  </div>
                  <div style={{marginBottom: 22}}>
                    <label style={{fontWeight: 700, fontSize: 16, marginRight: 12, display: 'inline-block', minWidth: 84}}>Группы:</label>
                    <div className="user-groups-picker">
                      <div ref={groupsDdRef} style={{ position: 'relative' }}>
                        <button
                          type="button"
                          onClick={() => setGroupsDdOpen(v => !v)}
                          className={`user-groups-trigger ${groupsDdOpen ? 'user-groups-trigger--open' : ''}`}
                        >
                          {editGroups.length > 0 ? `Выбрано: ${editGroups.length}` : 'Выбрать группы'}
                        </button>
                        {groupsDdOpen && (
                          <div className="user-groups-dd">
                            <div className="user-groups-dd-head">
                              <input
                                placeholder="Поиск группы"
                                value={groupsSearch}
                                onChange={e => setGroupsSearch(e.target.value)}
                                className="user-groups-search"
                              />
                              <button type="button" onClick={() => setEditGroups(groups.map(g => g._id))} className="user-groups-dd-btn">Все</button>
                              <button type="button" onClick={() => setEditGroups([])} className="user-groups-dd-btn user-groups-dd-btn--clear">Очистить</button>
                            </div>
                            <div className="user-groups-list">
                              {groups
                                .filter(g => g.name?.toLowerCase().includes(groupsSearch.trim().toLowerCase()))
                                .map(g => {
                                  const checked = editGroups.some(id => String(id) === String(g._id));
                                  return (
                                    <label key={g._id} className="user-groups-item">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          setEditGroups(prev => {
                                            const id = String(g._id);
                                            if (e.target.checked) return [...new Set([...prev.map(String), id])];
                                            return prev.filter(x => String(x) !== id);
                                          });
                                        }}
                                      />
                                      <span style={{ fontSize: 14 }}>{g.name}</span>
                                    </label>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        style={{ background: '#6c3be4', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontWeight: 600, height: 42, cursor: actionLoading ? 'not-allowed' : 'pointer' }}
                        disabled={actionLoading}
                        onClick={async () => {
                          setActionLoading(true);
                          try {
                            await updateStudent(user._id, { groups: editGroups });
                            await fetchStudents();
                          } finally {
                            setActionLoading(false);
                          }
                        }}
                      >Сохранить</button>
                    </div>
                    {editGroups.length > 0 && (
                      <div className="user-groups-chips">
                        {editGroups.map(id => {
                          const g = groups.find(x => String(x._id) === String(id));
                          if (!g) return null;
                          return (
                            <span key={id} className="user-groups-chip">
                              {g.name}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div style={{marginBottom: 22}}>
                    <label style={{fontWeight: 700, fontSize: 16, marginRight: 12, display: 'block', marginBottom: 8}}>Заметки (для админа):</label>
                    <textarea
                      value={adminNotes}
                      onChange={e => setAdminNotes(e.target.value)}
                      placeholder='Ваши пометки по ученику'
                      style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', minHeight: 120, padding: 10, borderRadius: 8, border: '1.5px solid #d1d1e0', background: '#fafaff', fontSize: 15 }}
                    />
                    <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                      <button
                        style={{background: '#6c3be4', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: actionLoading ? 'not-allowed' : 'pointer'}}
                        disabled={actionLoading}
                        onClick={async () => {
                          setActionLoading(true);
                          try {
                            await updateStudent(user._id, { adminNotes });
                            await fetchStudents();
                          } finally { setActionLoading(false); }
                        }}
                      >Сохранить заметки</button>
                      {user?.adminNotes && (
                        <button
                          style={{background: '#f5f5f5', color: '#222', border: '1px solid #dcdced', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: actionLoading ? 'not-allowed' : 'pointer'}}
                          disabled={actionLoading}
                          onClick={() => setAdminNotes('')}
                        >Очистить</button>
                      )}
                    </div>
                  </div>
                  <button
                    style={{marginBottom: 18, background: '#6c3be4', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 22px', fontWeight: 500, fontSize: 16, cursor: actionLoading ? 'not-allowed' : 'pointer', transition: 'background 0.2s'}}
                    disabled={actionLoading}
                    onClick={async () => {
                      if (!window.confirm('Сбросить пароль для этого ученика?')) return;
                      setActionLoading(true);
                      try {
                        const res = await resetStudentPassword(user._id);
                        setResetPassword(res.password);
                      } finally {
                        setActionLoading(false);
                      }
                    }}
                  >Сбросить пароль</button>
                  <button
                    style={{margin: '18px 0 0 0', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 0', fontWeight: 600, fontSize: 18, cursor: actionLoading ? 'not-allowed' : 'pointer', width: '100%', maxWidth: 340, transition: 'background 0.2s'}}
                    disabled={actionLoading}
                    onClick={async () => {
                      if (!window.confirm('Удалить ученика из базы? Это действие необратимо!')) return;
                      setActionLoading(true);
                      try {
                        await deleteStudent(user._id);
                        navigate('/admin/users');
                      } finally {
                        setActionLoading(false);
                      }
                    }}
                    onMouseOver={e => e.currentTarget.style.background = '#c0392b'}
                    onMouseOut={e => e.currentTarget.style.background = '#e74c3c'}
                  >Удалить ученика</button>
                  {/* Список файлов пользователя */}
                  <div style={{marginTop: 32}}>
                    <h3 style={{fontSize: 20, fontWeight: 700, marginBottom: 16}}>Все файлы пользователя</h3>
                    {user.userFiles && user.userFiles.length > 0 ? (
                      <ul style={{padding: 0, listStyle: 'none', margin: 0, maxWidth: 600}}>
                        {user.userFiles.slice().reverse().map((f, i) => (
                          <li key={f.fileUrl + i} style={{marginBottom: 18, display: 'flex', alignItems: 'center', gap: 16, background: '#f7f7f9', borderRadius: 10, padding: 12, boxShadow: '0 1px 6px #ece9f7', animation: 'fadeIn 0.4s'}}>
                            {f.fileName && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(f.fileName) ? (
                              <img src={`${getStaticBaseUrl()}${f.fileUrl}`} alt={f.fileName} style={{width: 48, height: 48, objectFit: 'cover', borderRadius: 8, boxShadow: '0 1px 4px #ddd'}} />
                            ) : (
                              <span style={{fontSize: 28, marginRight: 2}}>📎</span>
                            )}
                            <div style={{flex: 1, minWidth: 0}}>
                              <a href={`${getStaticBaseUrl()}${f.fileUrl}`} target="_blank" rel="noopener noreferrer" style={{color: '#6c3be4', textDecoration: 'underline', fontSize: 17, wordBreak: 'break-all', fontWeight: 500}}>{f.fileName}</a>
                              <div style={{color: '#888', fontSize: 13, marginTop: 2}}>{f.uploadedAt ? new Date(f.uploadedAt).toLocaleString() : ''}</div>
                              <div style={{color: '#888', fontSize: 13, marginTop: 2}}>
                                {f.week && <>Неделя: <b>{f.week}</b> </>}
                                {f.dayOfWeek && <>| День: <b>{f.dayOfWeek}</b> </>}
                                {f.startTime && f.endTime && <>| Время: <b>{f.startTime}-{f.endTime}</b> </>}
                              </div>
                              <div style={{color: '#888', fontSize: 13, marginTop: 2}}>
                                {f.subject && (
                                  <>Предмет: <b>{
                                    (typeof f.subject === 'object' && f.subject.name) ? f.subject.name :
                                    (subjects.find(s => String(s._id) === String(f.subject))?.name || f.subject)
                                  }</b> </>
                                )}
                                {f.groupId && <>| Группа: <b>{typeof f.groupId === 'object' ? f.groupId.name : f.groupId}</b> </>}
                              </div>
                              {f.comment && <div style={{color: '#6c3be4', fontSize: 13, marginTop: 2}}>Комментарий: {f.comment}</div>}
                            </div>
                             <a href={`${getStaticBaseUrl()}${f.fileUrl}`} download style={{background: '#6c3be4', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontWeight: 500, fontSize: 15, cursor: 'pointer', textDecoration: 'none', marginLeft: 8}}>Скачать</a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div style={{color: '#aaa', marginTop: 10}}>Нет файлов</div>
                    )}
                  </div>
            </div>
          ) : (
            <div style={{ color: '#888', marginTop: 24 }}>Пользователь не найден</div>
          )}
            </div>
            {/* Правая колонка — календарь оплат */}
            <div style={{ minWidth: 340, maxWidth: 400, width: '100%', background: '#fafaff', borderRadius: 16, boxShadow: '0 2px 16px #ece9f7', padding: '32px 24px', marginLeft: 0 }}>
              <h3 style={{fontSize: 20, fontWeight: 700, marginBottom: 16}}>Календарь оплат</h3>
              {loadingPayments ? (
                <div style={{ color: '#888' }}>Загрузка...</div>
              ) : (
                <>
                  <PaymentCalendar paymentHistory={paymentHistory} onChange={setPaymentHistory} editable />
                  <button onClick={handleSave} disabled={saving} style={{ background: '#1a7f1a', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 22px', fontWeight: 500, fontSize: 16, cursor: 'pointer', marginTop: 12 }}>
                    {saving ? 'Сохранение...' : 'Сохранить'}
                  </button>
                  {saveMsg && <span style={{ marginLeft: 16, color: saveMsg.startsWith('Ошибка') ? '#d32f2f' : '#1a7f1a', fontWeight: 500 }}>{saveMsg}</span>}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <AdminBottomNavBar />
    </div>
  );
}

export default AdminUserFull; 